'use client';

import { useCallback, useEffect, useState } from 'react';

import { ClickButton } from '@/components/click-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useLocalforage } from '@/hooks/use-localforage';

import { sendContactEmail } from '../server-actions';

const CONTACT_FORM_KEY = 'contact-form';
const DEFAULT_CONTACT_FORM = { firstName: '', lastName: '', email: '', phone: '', message: '', captcha: '' };
type ContactForm = typeof DEFAULT_CONTACT_FORM;

type ContactFormProps = {
  messagePlaceholder?: string;
};

export function ContactForm({ messagePlaceholder = 'I have questions about my ESA letter...' }: ContactFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [captchaNumbers, setCaptchaNumbers] = useState({ num1: 0, num2: 0 });
  const { values, setItem } = useLocalforage([CONTACT_FORM_KEY]);
  const contactForm = values[0] || DEFAULT_CONTACT_FORM;
  const setContactForm = useCallback((contactForm: ContactForm) => setItem(CONTACT_FORM_KEY, contactForm), [setItem]);
  const { toast } = useToast();

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setCaptchaNumbers({ num1, num2 });
  };

  return (
    <form
      className='grid grid-cols-1 md:grid-cols-2 max-w-md gap-4'
      onSubmit={async (e) => {
        e.preventDefault();

        if (parseInt(contactForm.captcha) !== captchaNumbers.num1 + captchaNumbers.num2) {
          toast({
            title: 'Invalid Captcha',
            description: 'Please solve the math problem correctly.',
          });
          generateCaptcha();
          setContactForm({ ...contactForm, captcha: '' });
          return;
        }

        setSubmitting(true);

        try {
          await sendContactEmail(contactForm);

          setContactForm({ ...contactForm, message: '', captcha: '' });
          generateCaptcha();
          toast({
            title: 'Message sent!',
            description: 'We will get back to you as soon as possible.',
          });
        } catch (error) {
          console.error(error);
          toast({
            title: 'Error',
            description: 'There was an error sending your message. Please try again later.',
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='firstName'>First Name</Label>
        <Input
          id='firstName'
          onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
          placeholder='Johnny'
          required
          type='text'
          value={contactForm.firstName}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='lastName'>Last Name</Label>
        <Input
          id='lastName'
          onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
          placeholder='Appleseed'
          required
          type='text'
          value={contactForm.lastName}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='email'>Email</Label>
        <Input
          id='email'
          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
          placeholder='jappleseed@gmail.com'
          required
          type='email'
          value={contactForm.email}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='phone'>Phone</Label>
        <Input
          id='phone'
          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value.replace(/[^0-9\-\+]/g, '') })}
          placeholder='xxx-xxx-xxxx'
          required
          type='tel'
          value={contactForm.phone}
        />
      </div>

      <div className='grid w-full max-w-md items-center gap-1.5 col-span-2'>
        <Label htmlFor='message'>Message</Label>
        <Textarea
          id='message'
          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
          placeholder={messagePlaceholder}
          required
          value={contactForm.message}
        />
      </div>

      <div className='flex flex-col gap-1.5 col-span-2'>
        <Label htmlFor='captcha'>
          Please solve: {captchaNumbers.num1} + {captchaNumbers.num2} = ?
        </Label>
        <Input
          id='captcha'
          onChange={(e) => setContactForm({ ...contactForm, captcha: e.target.value })}
          placeholder='Enter the sum'
          required
          type='number'
          value={contactForm.captcha}
        />
      </div>

      <ClickButton progress={submitting} type='submit'>
        Submit
      </ClickButton>
    </form>
  );
}
